import { EmployeeProductType } from '../../generated/api/employeeProductType.js';
import { DeviceListActionCoCo, DeviceListStatusCoCo } from '../enums.js';
import { monthsMsg, oneTimePaymentMsg, t } from '../i18n/index.js';
import type { EmployeeProductListRequest } from '../../generated/api/employeeProductListRequest.js';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { EmployeeProductSelectionRequest } from '../../generated/api/employeeProductSelectionRequest.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';
import type { PatchEmployeeProductListRequest } from '../../generated/api/patchEmployeeProductListRequest.js';
import type { ShouldRevalidateFunctionArgs } from 'react-router';

export const ONE_TIME_PAYMENT_CONTRACT_TERM = '1';

export const DEVICE_LIST_SUMMARY_PARAM = 'summary';

const withoutSummaryParam = (url: URL) => {
  const params = new URLSearchParams(url.search);
  params.delete(DEVICE_LIST_SUMMARY_PARAM);
  return params.toString();
};

// The form and its summary share the loader data, so moving between them must not refetch it
export const shouldRevalidateDeviceListNew = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean => {
  if (
    currentUrl.href !== nextUrl.href &&
    currentUrl.pathname === nextUrl.pathname &&
    withoutSummaryParam(currentUrl) === withoutSummaryParam(nextUrl)
  ) {
    return false;
  }
  return defaultShouldRevalidate;
};

const MONTHLY_CONTRACT_TERMS = ['12', '24', '36', '48'];

export const getDeviceListContractTerms = (productType?: EmployeeProductType) =>
  productType === EmployeeProductType.EPP
    ? MONTHLY_CONTRACT_TERMS
    : [ONE_TIME_PAYMENT_CONTRACT_TERM, ...MONTHLY_CONTRACT_TERMS];

// The given contract terms in the same order as in the device list form
export const getSelectedContractTerms = (productType: EmployeeProductType | undefined, contractTerms: string[]) =>
  getDeviceListContractTerms(productType).filter(contractTerm => contractTerms.includes(contractTerm));

export const getContractTermLabel = (contractTerm: string) =>
  contractTerm === ONE_TIME_PAYMENT_CONTRACT_TERM ? t.ASEI(oneTimePaymentMsg) : `${contractTerm} ${t.XXVX(monthsMsg)}`;

export const DEVICE_LIST_DAMAGE_INSURANCE_OPTIONS = [
  { label: 'Business Pro', value: 'Business_Pro' },
  { label: 'Business Premium', value: 'Business_Premium' },
];

export const DEVICE_LIST_PRODUCT_TYPES = [EmployeeProductType.EPP, EmployeeProductType.Purchased_Device];

// Company shares are keyed with a prefix, as react-hook-form would handle numeric keys as array indexes
export const getCompanyShareKey = (contractTerm: string) => `term${contractTerm}`;

export const PURCHASED_DEVICE_BILLING_FIELD_PREFIX = 'catalog.purchasedDeviceBilling';

export interface DeviceListBillingSettingsCoCo {
  billingAccountId?: string;
  approverMustSelectBillingAccount?: boolean;
  selectedBillingAccountIds?: string[];
}

export interface DeviceListFormValuesCoCo {
  catalog: {
    name: string;
    productType: EmployeeProductType;
    employeeProductSelection: string;
    contractTerms: string[];
    companyShare?: Record<string, string>;
    showPurchaseDevicePrice?: boolean;
    damageInsurance?: string[];
    corporateMessage?: string;
    enrollmentProgramConsent?: boolean;
    enrollmentProgramAlias?: string;
    billingAccountId?: string;
    approverMustSelectBillingAccount?: boolean;
    selectedBillingAccountIds?: string[];
    useDifferentPurchasedDeviceBillingAccount?: boolean;
    purchasedDeviceBilling?: DeviceListBillingSettingsCoCo;
  };
  isCorporateMessageAdded?: boolean;
}

const toEppBillingSettings = (billing: DeviceListBillingSettingsCoCo) => ({
  defaultBaEpp: billing.billingAccountId,
  approverMustSelectBaEpp: Boolean(billing.approverMustSelectBillingAccount),
  restrictedBaEpp: billing.selectedBillingAccountIds ?? [],
});

const toPurchasedDeviceBillingSettings = (billing: DeviceListBillingSettingsCoCo) => ({
  defaultBaPurchasedDevices: billing.billingAccountId,
  approverMustSelectBaPurchasedDevice: Boolean(billing.approverMustSelectBillingAccount),
  restrictedBaPurchasedDevices: billing.selectedBillingAccountIds ?? [],
});

// Purchased device lists only have the purchased device billing settings
export const toDeviceListBillingSettings = (list: EmployeeProductListResponse): DeviceListBillingSettingsCoCo =>
  list.productType === EmployeeProductType.EPP
    ? {
        billingAccountId: list.defaultBaEpp,
        approverMustSelectBillingAccount: list.approverMustSelectBaEpp,
        selectedBillingAccountIds: list.restrictedBaEpp,
      }
    : {
        billingAccountId: list.defaultBaPurchasedDevices,
        approverMustSelectBillingAccount: list.approverMustSelectBaPurchasedDevice,
        selectedBillingAccountIds: list.restrictedBaPurchasedDevices,
      };

export const toCompanyShare = (value?: string) => {
  const companyShare = Number.parseFloat(value ?? '');
  return Number.isNaN(companyShare) ? undefined : companyShare;
};

const toCompanySharePerContractTerm = (catalog: DeviceListFormValuesCoCo['catalog'], isEpp: boolean) =>
  getSelectedContractTerms(catalog.productType, catalog.contractTerms).map(contractTerm => ({
    contractTerm,
    companyShare: isEpp ? toCompanyShare(catalog.companyShare?.[getCompanyShareKey(contractTerm)]) : undefined,
  }));

const DEVICE_LIST_ACTION_STATUSES: Record<DeviceListActionCoCo, DeviceListStatusCoCo> = {
  [DeviceListActionCoCo.SAVE_DRAFT]: DeviceListStatusCoCo.DRAFT,
  [DeviceListActionCoCo.PUBLISH]: DeviceListStatusCoCo.PUBLISHED,
};

export const toDeviceListAction = (status: DeviceListStatusCoCo) =>
  status === DeviceListStatusCoCo.PUBLISHED ? DeviceListActionCoCo.PUBLISH : DeviceListActionCoCo.SAVE_DRAFT;

// Undefined for an unsupported action
export const toDeviceListStatus = (action: string): DeviceListStatusCoCo | undefined =>
  DEVICE_LIST_ACTION_STATUSES[action as DeviceListActionCoCo];

// EPP lists have purchased device billing settings only when separate ones are given
const getPurchasedDeviceBilling = (
  catalog: DeviceListFormValuesCoCo['catalog'],
  isEpp: boolean
): DeviceListBillingSettingsCoCo | undefined => {
  if (!isEpp) {
    return catalog;
  }
  return catalog.useDifferentPurchasedDeviceBillingAccount ? (catalog.purchasedDeviceBilling ?? {}) : undefined;
};

export const toEmployeeProductListRequest = (
  values: DeviceListFormValuesCoCo,
  accountMdmId: string,
  status: DeviceListStatusCoCo
): EmployeeProductListRequest => {
  const { catalog } = values;
  const isEpp = catalog.productType === EmployeeProductType.EPP;
  const purchasedDeviceBilling = getPurchasedDeviceBilling(catalog, isEpp);
  return {
    name: catalog.name,
    accountMdmId,
    productType: catalog.productType,
    status,
    employeeProductSelection: catalog.employeeProductSelection,
    // Only Elisa Devices Service lists have a company share
    companySharePerContractTerm: toCompanySharePerContractTerm(catalog, isEpp),
    // Only purchased device lists can show the purchased device price
    showPurchaseDevicePrice: !isEpp && Boolean(catalog.showPurchaseDevicePrice),
    damageInsuranceOptions: isEpp ? (catalog.damageInsurance ?? []) : [],
    customerAdminMessage: values.isCorporateMessageAdded ? catalog.corporateMessage : undefined,
    enrollmentProgram: Boolean(catalog.enrollmentProgramConsent),
    customerDeviceEnrollmentAlias: catalog.enrollmentProgramConsent ? catalog.enrollmentProgramAlias : undefined,
    ...(isEpp && toEppBillingSettings(catalog)),
    ...(purchasedDeviceBilling && toPurchasedDeviceBillingSettings(purchasedDeviceBilling)),
  };
};

// A patch changes only the given fields, so the fields that are not in use are sent as empty to clear their old values
export const toPatchEmployeeProductListRequest = (
  values: DeviceListFormValuesCoCo,
  status: DeviceListStatusCoCo
): PatchEmployeeProductListRequest => {
  const { accountMdmId: _accountMdmId, ...request } = toEmployeeProductListRequest(values, '', status);
  return {
    ...request,
    customerAdminMessage: request.customerAdminMessage ?? '',
    customerDeviceEnrollmentAlias: request.customerDeviceEnrollmentAlias ?? '',
    defaultBaEpp: request.defaultBaEpp ?? '',
    approverMustSelectBaEpp: Boolean(request.approverMustSelectBaEpp),
    restrictedBaEpp: request.restrictedBaEpp ?? [],
    defaultBaPurchasedDevices: request.defaultBaPurchasedDevices ?? '',
    approverMustSelectBaPurchasedDevice: Boolean(request.approverMustSelectBaPurchasedDevice),
    restrictedBaPurchasedDevices: request.restrictedBaPurchasedDevices ?? [],
  };
};

const toPurchasedDeviceBillingFormValues = (list: EmployeeProductListResponse): DeviceListBillingSettingsCoCo => ({
  billingAccountId: list.defaultBaPurchasedDevices,
  approverMustSelectBillingAccount: Boolean(list.approverMustSelectBaPurchasedDevice),
  selectedBillingAccountIds: list.restrictedBaPurchasedDevices ?? [],
});

// The form refers to the device selection with its id, the list response with its uuid
export const toDeviceListFormValues = (
  list: EmployeeProductListResponse,
  deviceSelections: EmployeeProductSelectionResponse[]
): DeviceListFormValuesCoCo => {
  const productType = list.productType ?? EmployeeProductType.EPP;
  const isEpp = productType === EmployeeProductType.EPP;
  const purchasedDeviceBilling = toPurchasedDeviceBillingFormValues(list);
  const useDifferentPurchasedDeviceBillingAccount = isEpp && Boolean(list.defaultBaPurchasedDevices);
  return {
    catalog: {
      name: list.name ?? '',
      productType,
      employeeProductSelection:
        deviceSelections.find(({ uuid }) => uuid === list.employeeProductSelectionUuid)?.id ?? '',
      contractTerms: getSelectedContractTerms(productType, list.contractTerms ?? []),
      companyShare: isEpp
        ? Object.fromEntries(
            Object.entries(list.companyShare ?? {}).map(([contractTerm, companyShare]) => [
              getCompanyShareKey(contractTerm),
              String(companyShare),
            ])
          )
        : undefined,
      showPurchaseDevicePrice: Boolean(list.showPurchaseDevicePrice),
      damageInsurance: list.damageInsuranceOptions ?? [],
      corporateMessage: list.customerAdminMessage || undefined,
      enrollmentProgramConsent: Boolean(list.enrollmentProgram),
      enrollmentProgramAlias: list.customerDeviceEnrollmentAlias || undefined,
      ...(isEpp
        ? {
            billingAccountId: list.defaultBaEpp,
            approverMustSelectBillingAccount: Boolean(list.approverMustSelectBaEpp),
            selectedBillingAccountIds: list.restrictedBaEpp ?? [],
          }
        : purchasedDeviceBilling),
      useDifferentPurchasedDeviceBillingAccount,
      purchasedDeviceBilling: useDifferentPurchasedDeviceBillingAccount ? purchasedDeviceBilling : undefined,
    },
    isCorporateMessageAdded: Boolean(list.customerAdminMessage),
  };
};

// A copy is created as a draft with the same name and settings as the source list. Billing accounts are company
// specific, so they are left out and the admin selects them before publishing the copy.
export const toCopyEmployeeProductListRequest = (
  list: EmployeeProductListResponse,
  accountMdmId: string,
  employeeProductSelectionId?: string
): EmployeeProductListRequest => {
  const {
    defaultBaEpp: _defaultBaEpp,
    restrictedBaEpp: _restrictedBaEpp,
    defaultBaPurchasedDevices: _defaultBaPurchasedDevices,
    restrictedBaPurchasedDevices: _restrictedBaPurchasedDevices,
    ...request
  } = toEmployeeProductListRequest(toDeviceListFormValues(list, []), accountMdmId, DeviceListStatusCoCo.DRAFT);
  return { ...request, employeeProductSelection: employeeProductSelectionId };
};

// The device selection is copied to the target company, as device selections are company specific
export const toCopyEmployeeProductSelectionRequest = (
  { name, description, productType, items }: EmployeeProductSelectionResponse,
  accountMdmId: string
): EmployeeProductSelectionRequest => ({
  name,
  description,
  accountMdmId,
  productType,
  active: true,
  items: items.map(({ variantGroupId, variantGroupName }) => ({ variantGroupId, variantGroupName })),
});
